import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Sparkles, MessageSquare, ThumbsUp, ThumbsDown, Share2, Clock, Send, RefreshCw, Trash2, ArrowRight, History, X, Bookmark, BookmarkCheck, Lightbulb, HelpCircle, TrendingUp, Zap, BarChart3, DollarSign, Maximize2, ArrowUpDown, Link, Copy, Image, Check, MoreVertical, Wind, Sun, MapPin, Activity, Info, Code, Shield, CheckCircle2, Upload } from 'lucide-react';
import { useChatGPT, Message } from '../../hooks/useChatGPT';
import { ReasoningDisplay, ReasoningStep } from '../ReasoningDisplay';
import { ChartsPanel } from '../ChartsPanel';
import { VariableType } from '../../hooks/useDashboardState';
// Type definitions
interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: Date;
  messages: Message[];
}
interface SavedKeyPoint {
  id: string;
  content: string;
  fullContent: string;
  timestamp: Date;
  messageIndex: number;
  type: 'general' | 'trade-idea';
}
interface ContextQuestion {
  id: string;
  question: string;
  placeholder: string;
}
interface SuggestedQuery {
  id: string;
  text: string;
}
interface ProTip {
  icon: any;
  title: string;
  description: string;
}
// Mock data and constants
const mockChatHistory: ChatHistoryItem[] = [];
const contextQuestions: ContextQuestion[] = [{
  id: 'timeframe',
  question: 'What timeframe are you working with?',
  placeholder: 'e.g., this week, next month, Q2, 7-day forecast'
}, {
  id: 'region',
  question: 'Which region(s) are you focused on?',
  placeholder: 'e.g., Europe, Nordics, Germany, Spain'
}, {
  id: 'focus',
  question: 'What are your key priorities?',
  placeholder: 'e.g., forecast accuracy, trading opportunities, model validation'
}, {
  id: 'risk',
  question: 'What is your risk/confidence threshold?',
  placeholder: 'e.g., conservative, 80%+ confidence, p-value < 0.05'
}, {
  id: 'dataSources',
  question: 'Which data sources or models are you using?',
  placeholder: 'e.g., ECMWF ensemble, EPT-2, historical spot prices'
}];
const suggestedQueries: SuggestedQuery[] = [{
  id: '1',
  text: 'What are the key weather drivers this week?'
}, {
  id: '2',
  text: 'Show me spread opportunities in Europe'
}, {
  id: '3',
  text: 'Where is volatility highest right now?'
}, {
  id: '4',
  text: 'What are the main forecast divergences?'
}];
const proTips: ProTip[] = [{
  icon: TrendingUp,
  title: 'Be Specific',
  description: 'Include timeframe, region, and what you\'re looking for. "German wind forecast next 3 days" beats "weather forecast".'
}, {
  icon: Zap,
  title: 'Ask Follow-ups',
  description: 'Drill deeper with "Why?" or "Show me the data". Oracle remembers context from your conversation.'
}, {
  icon: BarChart3,
  title: 'Compare Scenarios',
  description: 'Ask "What if wind drops 20%?" or "Compare high vs low solar cases" to stress-test your positions.'
}, {
  icon: DollarSign,
  title: 'Save Key Points',
  description: 'Click the bookmark icon on any response to save insights and trades for later. Build your own knowledge base.'
}];
// Helper functions
const stripMarkdown = (text: string): string => {
  // Guard against non-string values
  if (!text || typeof text !== 'string') {
    return '';
  }
  return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/^#+\s+/gm, '').replace(/^[\-•→]\s+/gm, '').replace(/\[(.+?)\]\(.+?\)/g, '$1').trim();
};
const splitIntoSections = (content: string): string[] => {
  // Simple split: Find first major heading after title to split intro from analysis
  const lines = content.split('\n');
  if (lines.length === 0) return [content];
  let splitIndex = -1;
  // Find the first ## heading after the initial content (skip first few lines for title)
  for (let i = 3; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('##') || line.match(/^\d+\.\s+\w+/)) {
      splitIndex = i;
      break;
    }
  }
  // If we found a split point, create 2 sections
  if (splitIndex > 0) {
    return [lines.slice(0, splitIndex).join('\n').trim(), lines.slice(splitIndex).join('\n').trim()];
  }
  // Otherwise return as single section
  return [content];
};
const isIntroSection = (content: string): boolean => {
  const lines = content.split('\n').filter(l => l.trim());
  // Check if it's short (likely just title + brief intro)
  if (content.length < 200) return true;
  // Check if it has no bullet points (detailed content usually has bullets)
  const hasBullets = lines.some(line => line.match(/^[\-•→]\s+/));
  if (!hasBullets && lines.length < 5) return true;
  // Check if it's mostly headings
  const headingLines = lines.filter(line => line.match(/^#{1,3}\s+/));
  if (headingLines.length > 0 && headingLines.length === lines.length) return true;
  return false;
};
const extractMultipleKeyContents = (fullContent: string, type: 'general' | 'trade-idea'): string[] => {
  const lines = fullContent.split('\n').filter(l => l.trim());
  const keyContents: string[] = [];
  if (type === 'trade-idea') {
    // Extract trade-related bullet points
    lines.forEach(line => {
      if (line.match(/^[\-•→]\s+/) && (line.toLowerCase().includes('long') || line.toLowerCase().includes('short') || line.toLowerCase().includes('buy') || line.toLowerCase().includes('sell') || line.toLowerCase().includes('trade'))) {
        keyContents.push(line.replace(/^[\-•→]\s+/, ''));
      }
    });
  } else {
    // Extract general insights (first few bullet points or key sentences)
    let count = 0;
    lines.forEach(line => {
      if (count < 3 && (line.match(/^[\-•→]\s+/) || line.match(/^#{2,3}\s+/))) {
        keyContents.push(line.replace(/^[\-•→]\s+/, '').replace(/^#{2,3}\s+/, ''));
        count++;
      }
    });
  }
  return keyContents.length > 0 ? keyContents : [fullContent.slice(0, 200)];
};
const extractBothCategories = (fullContent: string): {
  forecasts: string[];
  trades: string[];
} => {
  const lines = fullContent.split('\n').filter(l => l.trim());
  const forecasts: string[] = [];
  const trades: string[] = [];
  // NEW: Look for "Trading Strategy:" labels in paragraphs
  const tradingStrategyRegex = /Trading Strategy:\s*(.+?)(?=\n\n|$)/gis;
  const matches = fullContent.matchAll(tradingStrategyRegex);
  for (const match of matches) {
    if (match[1]) {
      trades.push(match[1].trim());
    }
  }
  // Also look for "Actionable Insight:" followed by trading content
  const actionableInsightRegex = /Actionable Insight:\s*(.+?)(?:\.\s*Trading Strategy:|$)/gis;
  const insightMatches = fullContent.matchAll(actionableInsightRegex);
  for (const match of insightMatches) {
    if (match[1] && match[1].trim()) {
      forecasts.push(match[1].trim());
    }
  }
  // Original logic: Look for bullet points with trading keywords
  lines.forEach(line => {
    const cleanLine = line.replace(/^[\-•→]\s+/, '');
    // Check for trading-related bullet points with expanded keywords
    if (line.match(/^[\-•→]\s+/) && (line.toLowerCase().includes('long') || line.toLowerCase().includes('short') || line.toLowerCase().includes('buy') || line.toLowerCase().includes('buying') || line.toLowerCase().includes('sell') || line.toLowerCase().includes('selling') || line.toLowerCase().includes('position') || line.toLowerCase().includes('capitalize') || line.toLowerCase().includes('trade'))) {
      trades.push(cleanLine);
    }
    // General insights from bullet points or headings
    else if (line.match(/^[\-•→]\s+/) || line.match(/^#{2,3}\s+/)) {
      forecasts.push(cleanLine.replace(/^#{2,3}\s+/, ''));
    }
  });
  return {
    forecasts: forecasts.slice(0, 3),
    trades: trades.slice(0, 3)
  };
};
// Helper function to generate reasoning steps for Oracle queries
const generateOracleReasoningSteps = (query: string): ReasoningStep[] => {
  const isComplexQuery = query.length > 50 || query.toLowerCase().includes('compare') || query.toLowerCase().includes('analyze') || query.toLowerCase().includes('spread') || query.toLowerCase().includes('volatility');
  const baseSteps: ReasoningStep[] = [{
    id: 'parse-query',
    title: 'Understanding your question',
    description: `Analyzing: "${query.slice(0, 60)}${query.length > 60 ? '...' : ''}"`,
    action: 'read',
    duration: 1,
    status: 'active',
    details: ['Extracting key entities and timeframes', 'Identifying regions and markets', 'Determining query intent']
  }, {
    id: 'gather-context',
    title: 'Gathering market data',
    description: 'Fetching current conditions and forecasts',
    action: 'search',
    duration: 2,
    status: 'pending',
    details: ['Loading spot prices and generation data', 'Retrieving weather forecasts', 'Checking model outputs']
  }];
  if (isComplexQuery) {
    baseSteps.push({
      id: 'analyze-patterns',
      title: 'Analyzing patterns',
      description: 'Comparing models and identifying trends',
      action: 'search',
      duration: 2,
      status: 'pending',
      details: ['EPT-2 model analysis', 'ECMWF ensemble comparison', 'Historical pattern matching']
    });
    baseSteps.push({
      id: 'calculate-impact',
      title: 'Calculating market impact',
      description: 'Estimating price movements and opportunities',
      action: 'analyze',
      status: 'pending',
      details: ['Price impact modeling', 'Spread opportunity identification', 'Risk assessment']
    });
  }
  baseSteps.push({
    id: 'generate-response',
    title: 'Generating insights',
    description: 'Creating actionable recommendations',
    action: 'create',
    status: 'pending',
    duration: 2
  });
  return baseSteps;
};
// ... existing interfaces and constants ...
// NEW: Props interface for OracleView
interface OracleViewProps {
  messages: Message[];
  sendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  suggestedFollowUps: string[];
  clearHistory: () => void;
  loadMessages: (messages: Message[]) => void;
  onModalOpenChange?: (isOpen: boolean) => void;
}
// --- Selection Menu Component ---
function SelectionMenu({
  position,
  onSave
}: {
  position: {
    top: number;
    left: number;
  } | null;
  onSave: (type: 'insight' | 'trade') => void;
}) {
  if (!position) return null;
  return createPortal(<motion.div initial={{
    opacity: 0,
    scale: 0.9,
    y: 10
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.9,
    y: 10
  }} style={{
    position: 'fixed',
    top: position.top,
    left: position.left,
    zIndex: 9999
  }} className="flex flex-col bg-slate-900 text-white rounded-lg shadow-xl overflow-hidden min-w-[180px]" data-id="element-2306">
      <button onClick={() => onSave('insight')} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800 transition-colors text-sm font-medium text-left" data-id="element-2307">
        <Lightbulb size={16} className="text-amber-400" data-id="element-2308" />
        Save as Insight
      </button>
      <div className="h-px bg-slate-800" data-id="element-2309" />
      <button onClick={() => onSave('trade')} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800 transition-colors text-sm font-medium text-left" data-id="element-2310">
        <TrendingUp size={16} className="text-emerald-400" data-id="element-2311" />
        Save as Trade
      </button>
    </motion.div>, document.body);
}
export function OracleView({
  messages,
  sendMessage,
  isLoading,
  suggestedFollowUps,
  clearHistory,
  loadMessages,
  onModalOpenChange
}: OracleViewProps) {
  const [query, setQuery] = useState('');
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [showContextHelper, setShowContextHelper] = useState(false);
  const [contextAnswers, setContextAnswers] = useState<Record<string, string>>({
    timeframe: 'this week',
    region: 'Europe',
    focus: 'volatility',
    risk: '',
    dataSources: ''
  });
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>(mockChatHistory);
  const [savedKeyPoints, setSavedKeyPoints] = useState<SavedKeyPoint[]>([]);
  const [selectedKeyPoint, setSelectedKeyPoint] = useState<SavedKeyPoint | null>(null);
  const [currentProTipIndex, setCurrentProTipIndex] = useState(0);
  const [selectedMessageToShare, setSelectedMessageToShare] = useState<{
    content: string;
    index: number;
  } | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [selectedForecast, setSelectedForecast] = useState<{
    type: 'wind' | 'solar' | 'temperature' | 'price' | 'map';
    region: string;
    title: string;
  } | null>(null);
  // NEW: Reasoning state
  const [reasoningSteps, setReasoningSteps] = useState<ReasoningStep[]>([]);
  const [showReasoning, setShowReasoning] = useState(false);
  // NEW: Guardrails state
  const [guardrailsText, setGuardrailsText] = useState('');
  const [activeRole, setActiveRole] = useState<string | null>(null);
  // NEW: Loading state for insight extraction
  const [isExtractingInsights, setIsExtractingInsights] = useState(false);
  const [extractionMessageIndex, setExtractionMessageIndex] = useState<number | null>(null);
  // NEW: Track if navbar is scrolled past for dynamic height
  const [isNavbarScrolledPast, setIsNavbarScrolledPast] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const historyButtonRef = useRef<HTMLDivElement>(null);
  // NEW: Track if user has scrolled up (to avoid auto-scrolling when reading)
  const isUserScrolling = useRef(false);
  const lastScrollTop = useRef(0);
  // NEW: Guardrail presets
  const guardrailPresets = [{
    id: 'trader',
    role: 'Trader',
    icon: TrendingUp,
    theme: 'green',
    content: `• Risk-Averse: Prioritize stable positions, avoid volatile markets
• Balanced: Mix of stability and opportunity capture
• Opportunistic: Willing to take calculated risks for higher returns
• Focus on energy market fundamentals and weather patterns
• Consider liquidity and market depth before entering positions`
  }, {
    id: 'meteorologist',
    role: 'Meteorologist',
    icon: Wind,
    theme: 'cyan',
    content: `• Only use forecasts with 70%+ model agreement
• Verify patterns across multiple data sources
• Flag extreme weather events for manual review
• Update forecasts every 6 hours minimum
• Document confidence levels for all predictions`
  }, {
    id: 'quant',
    role: 'Quants/Developers',
    icon: Code,
    theme: 'purple',
    content: `• Backtest strategies over minimum 3-year period
• Validate models on out-of-sample data
• Monitor for model drift weekly
• Document all assumptions and limitations
• Implement circuit breakers for anomalous behavior`
  }];
  const handlePresetSelect = (preset: (typeof guardrailPresets)[0]) => {
    setGuardrailsText(preset.content);
    setActiveRole(preset.id);
  };
  // Rotate pro tips every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProTipIndex(prev => (prev + 1) % proTips.length);
    }, 15000);
    return () => clearInterval(interval);
  }, []);
  // Track user scroll position to determine if they're reading history
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = scrollContainer;
      // User is "at bottom" if within 100px of the bottom
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      isUserScrolling.current = !isAtBottom;
      lastScrollTop.current = scrollTop;
    };
    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);
  // Auto-scroll to bottom when messages change - but only if user is at bottom
  useEffect(() => {
    if (scrollRef.current && !isUserScrolling.current) {
      // Use requestAnimationFrame to avoid layout thrashing
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [messages, isLoading]);
  // Handle forecast link clicks
  useEffect(() => {
    const handleForecastClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('forecast-link')) {
        const type = target.getAttribute('data-forecast-type') as 'wind' | 'solar' | 'temperature' | 'price' | 'map';
        const text = target.getAttribute('data-forecast-text') || '';
        // NEW: Get the message index to find the full message context
        let messageElement = target.closest('[data-message-index]') as HTMLElement;
        const messageIndex = messageElement?.getAttribute('data-message-index');
        // IMPROVED: Extract region from FULL MESSAGE CONTEXT first
        let region = '';
        // Strategy 1: Look at the full message content for region context
        if (messageIndex !== null && messageIndex !== undefined) {
          const msgIdx = parseInt(messageIndex, 10);
          if (!isNaN(msgIdx) && messages[msgIdx]) {
            const fullMessage = messages[msgIdx].content;
            // Expanded region list
            const regionPattern = /(Nordics?|Nordic countries|Scandinavia|Scandinavian|Germany|German|Spain|Spanish|UK|United Kingdom|British|France|French|Italy|Italian|Netherlands|Dutch|Belgium|Belgian|Austria|Austrian|Poland|Polish|Sweden|Swedish|Norway|Norwegian|Denmark|Danish|Finland|Finnish|Europe|European|ERCOT|Texas|MISO|PJM|CAISO|California)/i;
            // Look for region mentions BEFORE the forecast link in the message
            // This captures "In Germany, wind forecast shows..." patterns
            const beforeLinkText = fullMessage.substring(0, fullMessage.indexOf(text));
            const regionMatch = beforeLinkText.match(regionPattern);
            if (regionMatch) {
              region = regionMatch[1];
            } else {
              // Fallback: check entire message
              const anyMatch = fullMessage.match(regionPattern);
              if (anyMatch) {
                region = anyMatch[1];
              }
            }
          }
        }
        // Strategy 2: Extract from the link text itself (original logic)
        if (!region) {
          const regionPattern = /(Nordics?|Nordic countries|Scandinavia|Scandinavian|Germany|Spain|UK|United Kingdom|France|Italy|Netherlands|Belgium|Austria|Poland|Sweden|Norway|Denmark|Finland|Europe|European|ERCOT|Texas|MISO|PJM|CAISO|California)/i;
          // Pattern 1: "in [Region]", "for [Region]", "across [Region]"
          const prepositionMatch = text.match(new RegExp(`(?:in|for|across)\\s+(${regionPattern.source})`, 'i'));
          if (prepositionMatch) {
            region = prepositionMatch[1];
          } else {
            // Pattern 2: "[Region] [forecast/generation/etc]"
            const prefixMatch = text.match(new RegExp(`(${regionPattern.source})(?:'s)?\\s+(?:wind|solar|temperature|price|forecast|generation)`, 'i'));
            if (prefixMatch) {
              region = prefixMatch[1];
            } else {
              // Pattern 3: "[forecast/generation] [Region]"
              const suffixMatch = text.match(new RegExp(`(?:wind|solar|temperature|price|forecast|generation)\\s+(${regionPattern.source})`, 'i'));
              if (suffixMatch) {
                region = suffixMatch[1];
              } else {
                // Pattern 4: Just the region name anywhere in the text
                const anywhereMatch = text.match(regionPattern);
                if (anywhereMatch) {
                  region = anywhereMatch[1];
                }
              }
            }
          }
        }
        // Strategy 3: Check context answers for region
        if (!region && contextAnswers.region) {
          region = contextAnswers.region;
        }
        // Strategy 4: If still no region, DON'T set a fallback
        // Leave it empty so the chart uses the chat context
        if (!region) {
          region = ''; // Empty string - no region label shown
        }
        // Normalize region name (only if we have one)
        if (region) {
          region = region.charAt(0).toUpperCase() + region.slice(1).toLowerCase();
          // Normalize common variations
          if (region.toLowerCase().includes('nordic')) {
            region = 'Nordics';
          } else if (region.toLowerCase().includes('scandinavia')) {
            region = 'Scandinavia';
          }
        }
        handleOpenForecastModal({
          type,
          region,
          title: text
        });
      }
    };
    if (scrollRef.current) {
      scrollRef.current.addEventListener('click', handleForecastClick);
    }
    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener('click', handleForecastClick);
      }
    };
  }, [messages, contextAnswers]);
  // Load saved key points from localStorage on mount AND whenever component becomes visible
  useEffect(() => {
    const loadSavedKeyPoints = () => {
      const saved = localStorage.getItem('oracle-saved-keypoints');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setSavedKeyPoints(parsed.map((p: any) => ({
            ...p,
            timestamp: new Date(p.timestamp),
            type: p.type || 'general',
            fullContent: p.fullContent || p.content
          })));
        } catch (e) {
          console.error('Failed to load saved key points:', e);
          localStorage.removeItem('oracle-saved-keypoints');
          setSavedKeyPoints([]);
        }
      }
    };
    // Load immediately on mount
    loadSavedKeyPoints();
    // Also listen for storage events from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'oracle-saved-keypoints') {
        loadSavedKeyPoints();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Listen for custom event when returning to this view
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadSavedKeyPoints();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array - only set up listeners once
  // Save to localStorage whenever savedKeyPoints changes
  useEffect(() => {
    if (savedKeyPoints.length >= 0) {
      // Changed from > 0 to >= 0 to handle deletions
      try {
        localStorage.setItem('oracle-saved-keypoints', JSON.stringify(savedKeyPoints));
        // Dispatch storage event for cross-tab sync
        window.dispatchEvent(new Event('storage'));
      } catch (e) {
        console.error('Failed to save key points:', e);
      }
    }
  }, [savedKeyPoints]);
  // NEW: Toast state for send to overview
  const [showSendToast, setShowSendToast] = useState(false);
  const [sendToastMessage, setSendToastMessage] = useState('');
  // NEW: Handle send to overview
  const handleSendToOverview = () => {
    if (savedKeyPoints.length === 0) return;
    // Get existing insights from localStorage
    const existingInsights = localStorage.getItem('oracle-overview-insights');
    const existing = existingInsights ? JSON.parse(existingInsights) : [];
    // Add current saved key points with timestamp
    const newInsights = savedKeyPoints.map(kp => ({
      ...kp,
      sentToOverviewAt: new Date().toISOString()
    }));
    // Combine and save
    const updated = [...newInsights, ...existing];
    localStorage.setItem('oracle-overview-insights', JSON.stringify(updated));
    // Clear current saved key points
    setSavedKeyPoints([]);
    localStorage.setItem('oracle-saved-keypoints', JSON.stringify([]));
    // Show success toast
    setSendToastMessage(`${savedKeyPoints.length} insight${savedKeyPoints.length > 1 ? 's' : ''} sent to Overview`);
    setShowSendToast(true);
    setTimeout(() => setShowSendToast(false), 3000);
    // Dispatch event for Overview to refresh
    window.dispatchEvent(new Event('oracle-insights-updated'));
  };
  const handleSubmit = () => {
    if (!query.trim() || isLoading) return;
    // Generate reasoning steps for this query
    const steps = generateOracleReasoningSteps(query);
    setReasoningSteps(steps);
    setShowReasoning(true);
    // Enhance query with context if available - FIXED: Only include non-empty values
    let enhancedQuery = query;
    const nonEmptyContext = Object.entries(contextAnswers).filter(([_, value]) => value && value.trim() !== '');
    if (nonEmptyContext.length > 0) {
      const contextString = nonEmptyContext.map(([key, value]) => `${key}: ${value}`).join(', ');
      enhancedQuery = `${query}\n\n[Context: ${contextString}]`;
    }
    // Also include guardrails if set
    if (guardrailsText.trim()) {
      enhancedQuery += `\n\n[Guardrails: ${guardrailsText}]`;
    }
    sendMessage(enhancedQuery);
    setQuery('');
    setShowContextHelper(false);
  };
  // NEW: Wrapper function that always generates reasoning before sending
  const handleSendWithReasoning = (messageText: string) => {
    if (isLoading) return;
    // Generate reasoning steps for this query
    const steps = generateOracleReasoningSteps(messageText);
    setReasoningSteps(steps);
    setShowReasoning(true);
    // Enhance query with context if available
    let enhancedQuery = messageText;
    const nonEmptyContext = Object.entries(contextAnswers).filter(([_, value]) => value && value.trim() !== '');
    if (nonEmptyContext.length > 0) {
      const contextString = nonEmptyContext.map(([key, value]) => `${key}: ${value}`).join(', ');
      enhancedQuery = `${messageText}\n\n[Context: ${contextString}]`;
    }
    // Also include guardrails if set
    if (guardrailsText.trim()) {
      enhancedQuery += `\n\n[Guardrails: ${guardrailsText}]`;
    }
    sendMessage(enhancedQuery);
  };
  // NEW: Simulate reasoning step progression
  useEffect(() => {
    if (!isLoading || reasoningSteps.length === 0) {
      return;
    }
    const interval = setInterval(() => {
      setReasoningSteps(currentSteps => {
        const activeIndex = currentSteps.findIndex(s => s.status === 'active');
        const pendingIndex = currentSteps.findIndex(s => s.status === 'pending');
        // If there's an active step, complete it
        if (activeIndex !== -1) {
          const newSteps = [...currentSteps];
          newSteps[activeIndex].status = 'complete';
          // Activate next pending step
          if (pendingIndex !== -1) {
            newSteps[pendingIndex].status = 'active';
          }
          return newSteps;
        }
        return currentSteps;
      });
    }, 1500); // Progress every 1.5 seconds
    return () => clearInterval(interval);
  }, [isLoading, reasoningSteps.length]);
  // Hide reasoning when loading completes
  useEffect(() => {
    if (!isLoading && showReasoning) {
      // Keep it visible for a moment after completion
      const timeout = setTimeout(() => {
        setShowReasoning(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, showReasoning]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleSuggestedQuery = (text: string) => {
    setQuery(text);
  };
  // NEW: LLM-powered insight extraction
  const extractInsightsWithLLM = async (fullContent: string): Promise<{
    forecasts: string[];
    trades: string[];
  }> => {
    try {
      const extractionPrompt = `You are an expert at analyzing energy market and weather forecast responses to extract actionable insights and trading strategies.

Analyze the following Oracle response and extract:
1. **Key Insights** (2-4 items): Important market conditions, forecasts, patterns, or risks that traders should know
2. **Trade Strategies** (1-3 items): Specific actionable trading recommendations with entry/exit points

**EXTRACTION RULES:**
- Each insight should be ONE clear, concise sentence (max 150 characters)
- Each trade should include: action (BUY/SELL/LONG/SHORT), timing, price target, and confidence
- Focus on ACTIONABLE information only
- Skip generic statements or background information
- Preserve specific numbers, timeframes, and regions

**OUTPUT FORMAT (JSON):**
{
  "insights": [
    "Insight 1 text here",
    "Insight 2 text here"
  ],
  "trades": [
    "Trade 1 text here",
    "Trade 2 text here"
  ]
}

**ORACLE RESPONSE TO ANALYZE:**
${fullContent}

Return ONLY the JSON object, no additional text.`;
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: 'You are an expert at extracting actionable insights and trading strategies from energy market analysis. Always respond with valid JSON only.'
          }, {
            role: 'user',
            content: extractionPrompt
          }],
          temperature: 0.3,
          stream: false
        })
      });
      if (!response.ok) {
        throw new Error(`Extraction API failed: ${response.status}`);
      }
      const data = await response.json();
      const content = data?.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('No content in extraction response');
      }
      // Parse the JSON response
      const parsed = JSON.parse(content.trim());
      return {
        forecasts: parsed.insights || [],
        trades: parsed.trades || []
      };
    } catch (error) {
      console.error('LLM extraction failed:', error);
      // Fallback to regex extraction
      return extractBothCategories(fullContent);
    }
  };
  const handleSaveKeyPoint = async (fullContent: string, messageIndex: number, type: 'general' | 'trade-idea' = 'general') => {
    // Show loading state
    setIsExtractingInsights(true);
    setExtractionMessageIndex(messageIndex);
    try {
      // Use LLM to extract insights and trades
      const {
        forecasts,
        trades
      } = await extractInsightsWithLLM(fullContent);
      const newKeyPoints: SavedKeyPoint[] = [];
      // Add forecast/insight cards
      forecasts.forEach((content, idx) => {
        newKeyPoints.push({
          id: `kp-forecast-${Date.now()}-${idx}`,
          content: content,
          fullContent: fullContent,
          timestamp: new Date(),
          messageIndex,
          type: 'general'
        });
      });
      // Add trade recommendation cards
      trades.forEach((content, idx) => {
        newKeyPoints.push({
          id: `kp-trade-${Date.now()}-${idx}`,
          content: content,
          fullContent: fullContent,
          timestamp: new Date(),
          messageIndex,
          type: 'trade-idea'
        });
      });
      // Fallback: if nothing extracted, use old method
      if (newKeyPoints.length === 0) {
        const extractedContents = extractMultipleKeyContents(fullContent, type);
        extractedContents.forEach((content, idx) => {
          newKeyPoints.push({
            id: `kp-${Date.now()}-${idx}`,
            content: content,
            fullContent: fullContent,
            timestamp: new Date(),
            messageIndex,
            type
          });
        });
      }
      const updated = [...newKeyPoints, ...savedKeyPoints];
      setSavedKeyPoints(updated);
      localStorage.setItem('oracle-saved-keypoints', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save key point:', error);
      // Still save with fallback extraction on error
      const {
        forecasts,
        trades
      } = extractBothCategories(fullContent);
      const newKeyPoints: SavedKeyPoint[] = [];
      forecasts.forEach((content, idx) => {
        newKeyPoints.push({
          id: `kp-forecast-${Date.now()}-${idx}`,
          content: content,
          fullContent: fullContent,
          timestamp: new Date(),
          messageIndex,
          type: 'general'
        });
      });
      trades.forEach((content, idx) => {
        newKeyPoints.push({
          id: `kp-trade-${Date.now()}-${idx}`,
          content: content,
          fullContent: fullContent,
          timestamp: new Date(),
          messageIndex,
          type: 'trade-idea'
        });
      });
      const updated = [...newKeyPoints, ...savedKeyPoints];
      setSavedKeyPoints(updated);
      localStorage.setItem('oracle-saved-keypoints', JSON.stringify(updated));
    } finally {
      setIsExtractingInsights(false);
      setExtractionMessageIndex(null);
    }
  };
  const handleDeleteKeyPoint = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const updated = savedKeyPoints.filter(kp => kp.id !== id);
    setSavedKeyPoints(updated);
    localStorage.setItem('oracle-saved-keypoints', JSON.stringify(updated));
  };
  const isKeypointSaved = (messageIndex: number) => {
    return savedKeyPoints.some(kp => kp.messageIndex === messageIndex);
  };
  const isTradeIdeaSaved = (messageIndex: number) => {
    return savedKeyPoints.some(kp => kp.messageIndex === messageIndex && kp.type === 'trade-idea');
  };
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const handleShareMessage = (content: string, index: number) => {
    setSelectedMessageToShare({
      content,
      index
    });
    setShowShareModal(true);
  };
  const handleCopyURL = () => {
    const url = `${window.location.origin}${window.location.pathname}?message=${selectedMessageToShare?.index}`;
    navigator.clipboard.writeText(url);
    setCopySuccess('URL copied!');
    setTimeout(() => setCopySuccess(null), 2000);
  };
  const handleCopyText = () => {
    if (selectedMessageToShare) {
      navigator.clipboard.writeText(stripMarkdown(selectedMessageToShare.content));
      setCopySuccess('Text copied!');
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };
  const handleDownloadImage = () => {
    // Future: implement html2canvas or similar
    setCopySuccess('Image download coming soon!');
    setTimeout(() => setCopySuccess(null), 2000);
  };
  // Simple markdown renderer optimized for concise trader responses
  const renderMarkdown = (content: string, highlightText?: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let listKey = 0;
    // Normalize text for comparison (remove markdown, extra spaces)
    const normalizeForComparison = (text: string) => {
      return stripMarkdown(text).toLowerCase().trim().replace(/\s+/g, ' ');
    };
    const highlightNormalized = highlightText ? normalizeForComparison(highlightText) : null;
    // Check if a line contains the highlight text
    const shouldHighlight = (text: string) => {
      if (!highlightNormalized) return false;
      const normalized = normalizeForComparison(text);
      return normalized.includes(highlightNormalized) || highlightNormalized.includes(normalized);
    };
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(<ul key={`list-${listKey++}`} className="space-y-1.5 my-3 ml-1" data-id="element-2312">
            {currentList.map((item, idx) => {
            const isHighlighted = shouldHighlight(item);
            return <li key={idx} className={`flex items-start gap-2.5 ${isHighlighted ? 'bg-amber-50 -mx-3 px-3 py-2 rounded-lg border-l-4 border-amber-400 shadow-sm' : ''}`} data-id="element-2313">
                  <span className={`mt-1 font-bold text-xs ${isHighlighted ? 'text-amber-600' : 'text-pink-600'}`} data-id="element-2314">
                    →
                  </span>
                  <span className="flex-1" dangerouslySetInnerHTML={{
                __html: formatInlineMarkdown(item)
              }} data-id="element-2315" />
                </li>;
          })}
          </ul>);
        currentList = [];
      }
    };
    const formatInlineMarkdown = (text: string) => {
      // Bold: **text** or __text__
      text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>');
      text = text.replace(/__(.+?)__/g, '<strong class="font-bold text-slate-900">$1</strong>');
      // Italic: *text* or _text_ (but not ** or __)
      text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em class="italic text-slate-700">$1</em>');
      text = text.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em class="italic text-slate-700">$1</em>');
      // Detect and linkify forecast references
      // Wind patterns: "wind forecast", "wind generation", "wind speed"
      text = text.replace(/(wind\s+(?:forecast|generation|speed|output|pattern|power)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi, '<button class="forecast-link text-cyan-600 hover:text-cyan-700 underline decoration-dotted underline-offset-2 font-medium text-left inline-block" data-forecast-type="wind" data-forecast-text="$1">$1</button>');
      // Solar patterns
      text = text.replace(/(solar\s+(?:forecast|generation|irradiance|output|power)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi, '<button class="forecast-link text-orange-600 hover:text-orange-700 underline decoration-dotted underline-offset-2 font-medium text-left inline-block" data-forecast-type="solar" data-forecast-text="$1">$1</button>');
      // Temperature patterns
      text = text.replace(/(temperature\s+(?:forecast|trend|pattern|drop|rise)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi, '<button class="forecast-link text-purple-600 hover:text-purple-700 underline decoration-dotted underline-offset-2 font-medium text-left inline-block" data-forecast-type="temperature" data-forecast-text="$1">$1</button>');
      // Price patterns
      text = text.replace(/((?:spot\s+)?price\s+(?:forecast|spike|trend|movement)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi, '<button class="forecast-link text-blue-600 hover:text-blue-700 underline decoration-dotted underline-offset-2 font-medium text-left inline-block" data-forecast-type="price" data-forecast-text="$1">$1</button>');
      // Regional/map patterns
      text = text.replace(/((?:across|throughout)\s+(?:Europe|European\s+markets|Germany|Spain|UK|France))/gi, '<button class="forecast-link text-emerald-600 hover:text-emerald-700 underline decoration-dotted underline-offset-2 font-medium text-left inline-block" data-forecast-type="map" data-forecast-text="$1">$1</button>');
      return text;
    };
    lines.forEach((line, idx) => {
      const isHighlighted = shouldHighlight(line);
      if (line.startsWith('###')) {
        flushList();
        const text = line.replace(/^###\s*/, '');
        elements.push(<h3 key={`h3-${idx}`} className={`text-sm font-bold text-slate-900 mt-3 mb-2 ${isHighlighted ? 'bg-amber-50 -mx-3 px-3 py-2 rounded-lg border-l-4 border-amber-400' : ''}`} dangerouslySetInnerHTML={{
          __html: formatInlineMarkdown(text)
        }} data-id="element-2316" />);
      } else if (line.startsWith('##')) {
        flushList();
        const text = line.replace(/^##\s*/, '');
        elements.push(<h2 key={`h2-${idx}`} className={`text-base font-bold text-slate-900 mt-4 mb-2 ${isHighlighted ? 'bg-amber-50 -mx-3 px-3 py-2 rounded-lg border-l-4 border-amber-400' : ''}`} dangerouslySetInnerHTML={{
          __html: formatInlineMarkdown(text)
        }} data-id="element-2317" />);
      } else if (line.match(/^[\-•→]\s+/)) {
        const text = line.replace(/^[\-•→]\s+/, '');
        currentList.push(text);
      } else if (line.trim() === '') {
        flushList();
        elements.push(<div key={`space-${idx}`} className="h-2" data-id="element-2318" />);
      } else {
        flushList();
        elements.push(<p key={`p-${idx}`} className={`leading-relaxed text-slate-700 ${isHighlighted ? 'bg-amber-50 -mx-3 px-3 py-2 rounded-lg border-l-4 border-amber-400 font-medium' : ''}`} dangerouslySetInnerHTML={{
          __html: formatInlineMarkdown(line)
        }} data-id="element-2319" />);
      }
    });
    flushList();
    return elements;
  };
  const currentProTip = proTips[currentProTipIndex];
  // Helper to get variable type for ChartsPanel
  const getVariableType = (type: string): VariableType => {
    switch (type) {
      case 'wind':
        return 'Wind';
      case 'solar':
        return 'Solar';
      case 'temperature':
        return 'Temperature';
      default:
        return 'Wind';
    }
  };
  // NEW: Selection state
  const [selectionMenu, setSelectionMenu] = useState<{
    visible: boolean;
    position: {
      top: number;
      left: number;
    } | null;
    text: string;
    source: string;
  }>({
    visible: false,
    position: null,
    text: '',
    source: ''
  });
  // NEW: Text selection logic
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        setTimeout(() => {
          const currentSel = window.getSelection();
          if (!currentSel || currentSel.isCollapsed) {
            setSelectionMenu(prev => ({
              ...prev,
              visible: false
            }));
          }
        }, 200);
        return;
      }
      const text = selection.toString().trim();
      if (text.length < 5) return;
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      // Find source
      let source = 'Oracle Response';
      let element = selection.anchorNode?.parentElement;
      while (element) {
        if (element.getAttribute('data-source')) {
          source = element.getAttribute('data-source') || 'Oracle Response';
          break;
        }
        element = element.parentElement;
      }
      setSelectionMenu({
        visible: true,
        position: {
          top: rect.bottom + window.scrollY + 10,
          left: rect.left + window.scrollX + rect.width / 2 - 90
        },
        text,
        source
      });
    };
    document.addEventListener('mouseup', handleSelectionChange);
    return () => document.removeEventListener('mouseup', handleSelectionChange);
  }, []);
  // NEW: Handle save from selection
  const handleSaveFromSelection = (type: 'insight' | 'trade') => {
    if (!selectionMenu.text) return;
    const newKeyPoint: SavedKeyPoint = {
      id: `kp-${Date.now()}`,
      content: selectionMenu.text,
      fullContent: selectionMenu.text,
      timestamp: new Date(),
      messageIndex: -1,
      type: type === 'trade' ? 'trade-idea' : 'general'
    };
    const updated = [newKeyPoint, ...savedKeyPoints];
    setSavedKeyPoints(updated);
    localStorage.setItem('oracle-saved-keypoints', JSON.stringify(updated));
    setSelectionMenu(prev => ({
      ...prev,
      visible: false
    }));
    window.getSelection()?.removeAllRanges();
  };
  // NEW: Handle context modal open/close
  const handleContextHelperChange = (isOpen: boolean) => {
    setShowContextHelper(isOpen);
    if (onModalOpenChange) {
      onModalOpenChange(isOpen);
    }
  };
  // NEW: Handle key point modal open/close
  const handleOpenKeyPointModal = (keyPoint: any) => {
    setSelectedKeyPoint(keyPoint);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };
  const handleCloseKeyPointModal = () => {
    setSelectedKeyPoint(null);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };
  // NEW: Handle forecast modal open/close
  const handleOpenForecastModal = (forecast: any) => {
    setSelectedForecast(forecast);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };
  const handleCloseForecastModal = () => {
    setSelectedForecast(null);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };
  // NEW: Handle share modal open/close
  const handleOpenShareModal = (content: string, index: number) => {
    setSelectedMessageToShare({
      content,
      index
    });
    setShowShareModal(true);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };
  const handleCloseShareModal = () => {
    setShowShareModal(false);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };
  // NEW: Track window scroll to detect when navbar is scrolled past
  useEffect(() => {
    const handleWindowScroll = () => {
      // TopBar height is 64px (h-16), check if we've scrolled past it
      const scrolledPast = window.scrollY > 64;
      setIsNavbarScrolledPast(scrolledPast);
    };
    // Initial check
    handleWindowScroll();
    window.addEventListener('scroll', handleWindowScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, []);
  // Calculate dynamic height and top position based on scroll state
  const containerHeight = isNavbarScrolledPast ? 'h-screen' : 'h-[calc(100vh-80px)]';
  const containerMaxHeight = isNavbarScrolledPast ? 'max-h-screen' : 'max-h-[calc(100vh-80px)]';
  const sidebarTop = isNavbarScrolledPast ? 'top-6' : 'top-[96px]';
  const sidebarBottom = isNavbarScrolledPast ? 'bottom-6' : 'bottom-6';
  return <div className={`flex flex-col ${containerHeight} ${containerMaxHeight} overflow-hidden transition-all duration-300`} data-id="element-2320">
      {/* Compact Header - Removed layout prop to prevent scroll flickering */}
      <div className="flex-shrink-0 z-50 px-6 pt-4" data-id="element-2321">
        <AnimatePresence data-id="element-2322">
          {messages.length === 0 && <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="mb-4" data-id="element-2323">
              <div className="text-center" data-id="element-2324">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-100 to-purple-100 mb-2 shadow-lg shadow-pink-500/20" data-id="element-2325">
                  <Sparkles className="text-pink-600" size={18} data-id="element-2326" />
                </div>
                <h1 className="text-xl font-bold text-slate-900 mb-1" data-id="element-2327">
                  Jua Oracle
                </h1>
                <p className="text-slate-600 text-xs max-w-2xl mx-auto mb-3" data-id="element-2328">
                  Fast, actionable insights for energy traders. Ask specific
                  questions, get concise answers.
                </p>

                {/* Context Helper Prompt */}
                <button onClick={() => setShowContextHelper(!showContextHelper)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 rounded-xl text-xs font-medium transition-all" data-id="element-2329">
                  <HelpCircle size={14} data-id="element-2330" />
                  Set Context for Better Answers
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {messages.length > 0 && <div className="flex justify-center mb-4" data-id="element-2331">
            <div className="w-full max-w-6xl flex items-center justify-between" data-id="element-2332">
              <div data-id="element-2333">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-2334">
                  <Sparkles className="text-pink-600" size={18} data-id="element-2335" />
                  Jua Oracle
                </h2>
                <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-2336">
                  Concise, trader-focused insights
                </p>
              </div>

              <button onClick={() => handleContextHelperChange(!showContextHelper)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/80 backdrop-blur-sm border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-slate-700 hover:text-blue-700 shadow-sm" data-id="element-2337">
                <HelpCircle size={14} data-id="element-2338" />
                <span className="text-xs font-medium" data-id="element-2339">Context</span>
              </button>
            </div>
          </div>}
      </div>

      {/* Context Helper Modal */}
      <AnimatePresence data-id="element-2340">
        {showContextHelper && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]" onClick={() => handleContextHelperChange(false)} data-id="element-2341" />
            <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none" data-id="element-2342">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2343">
                <div className="p-6 border-b border-slate-100" data-id="element-2344">
                  <div className="flex items-start justify-between" data-id="element-2345">
                    <div data-id="element-2346">
                      <h3 className="text-xl font-bold text-slate-900 mb-1" data-id="element-2347">
                        Set Your Context
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-2348">
                        Customize Oracle's responses to your needs
                      </p>
                    </div>
                    <button onClick={() => handleContextHelperChange(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2349">
                      <X size={20} className="text-slate-400" data-id="element-2350" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-2351">
                  {/* Simplified two-column grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-2352">
                    {contextQuestions.map(q => <div key={q.id} className={q.id === 'focus' || q.id === 'dataSources' || q.id === 'risk' ? 'md:col-span-2' : ''} data-id="element-2353">
                        <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide" data-id="element-2354">
                          {q.question}
                        </label>
                        <input type="text" value={contextAnswers[q.id] || ''} onChange={e => setContextAnswers({
                    ...contextAnswers,
                    [q.id]: e.target.value
                  })} placeholder={q.placeholder} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition-all" data-id="element-2355" />
                      </div>)}
                  </div>

                  {/* Guardrails Section - Simplified */}
                  <div className="pt-6 border-t border-slate-100" data-id="element-2356">
                    <div className="flex items-center gap-2 mb-4" data-id="element-2357">
                      <Shield size={18} className="text-indigo-600" data-id="element-2358" />
                      <div data-id="element-2359">
                        <h4 className="text-sm font-bold text-slate-900" data-id="element-2360">
                          Guardrails & Guidelines
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-2361">
                          Optional: Define operational constraints
                        </p>
                      </div>
                    </div>

                    {/* Role preset buttons - horizontal */}
                    <div className="flex gap-2 mb-3" data-id="element-2362">
                      {guardrailPresets.map(preset => <button key={preset.id} onClick={() => handlePresetSelect(preset)} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium ${activeRole === preset.id ? preset.theme === 'green' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : preset.theme === 'cyan' ? 'bg-cyan-50 border-cyan-300 text-cyan-700' : 'bg-purple-50 border-purple-300 text-purple-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-2363">
                          <preset.icon size={16} data-id="element-2364" />
                          {preset.role}
                        </button>)}
                    </div>

                    <textarea value={guardrailsText} onChange={e => {
                  setGuardrailsText(e.target.value);
                  setActiveRole(null);
                }} placeholder="Enter custom guardrails or select a role preset above..." className="w-full h-32 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none resize-none transition-all" data-id="element-2365" />
                  </div>
                </div>

                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center gap-3" data-id="element-2366">
                  <button onClick={() => {
                setContextAnswers({
                  timeframe: '',
                  region: '',
                  focus: '',
                  risk: '',
                  dataSources: ''
                });
                setGuardrailsText('');
                setActiveRole(null);
              }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2367">
                    Clear All
                  </button>
                  <div className="flex gap-2" data-id="element-2368">
                    <button onClick={() => handleContextHelperChange(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-2369">
                      Cancel
                    </button>
                    <button onClick={() => handleContextHelperChange(false)} className="px-5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-blue-500/25 transition-all" data-id="element-2370">
                      Apply Context
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Key Point Detail Modal - Now shows full content WITH HIGHLIGHTING */}
      <AnimatePresence data-id="element-2371">
        {selectedKeyPoint && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50" onClick={handleCloseKeyPointModal} data-id="element-2372" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2373">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2374">
                <div className={`p-6 border-b border-slate-200 ${selectedKeyPoint.type === 'trade-idea' ? 'bg-gradient-to-r from-emerald-50 to-green-50' : 'bg-gradient-to-r from-pink-50 to-purple-50'}`} data-id="element-2375">
                  <div className="flex items-start justify-between" data-id="element-2376">
                    <div className="flex items-center gap-3" data-id="element-2377">
                      <div className={`p-2.5 rounded-xl ${selectedKeyPoint.type === 'trade-idea' ? 'bg-emerald-100' : 'bg-pink-100'}`} data-id="element-2378">
                        {selectedKeyPoint.type === 'trade-idea' ? <ArrowUpDown size={24} className="text-emerald-600" data-id="element-2379" /> : <Bookmark size={24} className="text-pink-600" data-id="element-2380" />}
                      </div>
                      <div data-id="element-2381">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-2382">
                          Insight in Context
                        </h3>
                        <p className="text-sm text-slate-600" data-id="element-2383">
                          Saved {formatTimeAgo(selectedKeyPoint.timestamp)} •
                          Highlighted below
                        </p>
                      </div>
                    </div>
                    <button onClick={handleCloseKeyPointModal} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2384">
                      <X size={20} className="text-slate-400" data-id="element-2385" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-2386">
                  {/* Highlight indicator */}
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-id="element-2387">
                    <Lightbulb size={16} className="text-amber-600 mt-0.5 flex-shrink-0" data-id="element-2388" />
                    <div className="text-xs text-amber-900" data-id="element-2389">
                      <p className="font-semibold mb-1" data-id="element-2390">
                        Your saved insight is highlighted in yellow below
                      </p>
                      <p className="text-amber-700" data-id="element-2391">
                        The full response is shown for context
                      </p>
                    </div>
                  </div>

                  <div className="prose prose-sm max-w-none" data-id="element-2392">
                    <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-2393">
                      {renderMarkdown(selectedKeyPoint.fullContent, selectedKeyPoint.content)}
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-2394">
                  <button onClick={() => {
                handleDeleteKeyPoint(selectedKeyPoint.id);
                setSelectedKeyPoint(null);
              }} className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" data-id="element-2395">
                    Delete
                  </button>
                  <button onClick={handleCloseKeyPointModal} className="px-6 py-2 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-all" data-id="element-2396">
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Forecast Chart/Map Modal */}
      <AnimatePresence data-id="element-2397">
        {selectedForecast && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={handleCloseForecastModal} data-id="element-2398" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2399">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2400">
                {/* Header */}
                <div className={`p-6 border-b border-slate-200 ${selectedForecast.type === 'wind' ? 'bg-gradient-to-r from-cyan-50 to-blue-50' : selectedForecast.type === 'solar' ? 'bg-gradient-to-r from-orange-50 to-yellow-50' : selectedForecast.type === 'temperature' ? 'bg-gradient-to-r from-purple-50 to-pink-50' : selectedForecast.type === 'price' ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'bg-gradient-to-r from-emerald-50 to-green-50'}`} data-id="element-2401">
                  <div className="flex items-start justify-between" data-id="element-2402">
                    <div className="flex items-center gap-3" data-id="element-2403">
                      <div className={`p-2.5 rounded-xl ${selectedForecast.type === 'wind' ? 'bg-cyan-100' : selectedForecast.type === 'solar' ? 'bg-orange-100' : selectedForecast.type === 'temperature' ? 'bg-purple-100' : selectedForecast.type === 'price' ? 'bg-blue-100' : 'bg-emerald-100'}`} data-id="element-2404">
                        {selectedForecast.type === 'wind' && <Wind size={24} className="text-cyan-600" data-id="element-2405" />}
                        {selectedForecast.type === 'solar' && <Sun size={24} className="text-orange-600" data-id="element-2406" />}
                        {selectedForecast.type === 'temperature' && <Activity size={24} className="text-purple-600" data-id="element-2407" />}
                        {selectedForecast.type === 'price' && <Zap size={24} className="text-blue-600" data-id="element-2408" />}
                        {selectedForecast.type === 'map' && <MapPin size={24} className="text-emerald-600" data-id="element-2409" />}
                      </div>
                      <div data-id="element-2410">
                        <h3 className="text-lg font-bold text-slate-900 capitalize" data-id="element-2411">
                          {selectedForecast.type === 'map' ? 'Regional Overview' : `${selectedForecast.type} Forecast`}
                        </h3>
                        <p className="text-sm text-slate-600" data-id="element-2412">
                          {selectedForecast.region ? `${selectedForecast.region} • ` : ''}
                          Next 7 days
                        </p>
                      </div>
                    </div>
                    <button onClick={handleCloseForecastModal} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2413">
                      <X size={20} className="text-slate-400" data-id="element-2414" />
                    </button>
                  </div>
                </div>

                {/* Chart Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar h-[500px]" data-id="element-2415">
                  {selectedForecast.type === 'map' ? <div className="space-y-4" data-id="element-2416">
                      <div className="bg-slate-50 rounded-xl p-8 border border-slate-200" data-id="element-2417">
                        <div className="text-center text-slate-500" data-id="element-2418">
                          <MapPin size={48} className="mx-auto mb-4 text-slate-400" data-id="element-2419" />
                          <p className="font-medium" data-id="element-2420">Interactive Map View</p>
                          <p className="text-sm mt-2" data-id="element-2421">
                            Regional forecast visualization across{' '}
                            {selectedForecast.region}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2422">
                        {['Germany', 'Spain', 'UK', 'France'].map(country => <div key={country} className="bg-white border border-slate-200 rounded-lg p-4" data-id="element-2423">
                            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-2424">
                              {country}
                            </h4>
                            <div className="space-y-1 text-sm" data-id="element-2425">
                              <div className="flex justify-between" data-id="element-2426">
                                <span className="text-slate-600" data-id="element-2427">
                                  Avg Wind:
                                </span>
                                <span className="font-medium" data-id="element-2428">
                                  {(Math.random() * 10 + 5).toFixed(1)} m/s
                                </span>
                              </div>
                              <div className="flex justify-between" data-id="element-2429">
                                <span className="text-slate-600" data-id="element-2430">
                                  Avg Solar:
                                </span>
                                <span className="font-medium" data-id="element-2431">
                                  {(Math.random() * 400 + 400).toFixed(0)} W/m²
                                </span>
                              </div>
                              <div className="flex justify-between" data-id="element-2432">
                                <span className="text-slate-600" data-id="element-2433">
                                  Avg Price:
                                </span>
                                <span className="font-medium" data-id="element-2434">
                                  €{(Math.random() * 40 + 60).toFixed(2)}/MWh
                                </span>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div> : <div className="space-y-6" data-id="element-2435">
                      {/* Real Chart from ChartsPanel */}
                      <div className="h-[400px]" data-id="element-2436">
                        <ChartsPanel activeRegion={selectedForecast.region} activeVariable={getVariableType(selectedForecast.type)} dashboardContext={{
                    defaultTitle: selectedForecast.title
                  }} data-id="element-2437" />
                      </div>

                      {/* Data Table */}
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-2438">
                        <table className="w-full text-sm" data-id="element-2439">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2440">
                            <tr data-id="element-2441">
                              <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2442">
                                Day
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2443">
                                Min
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2444">
                                Max
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2445">
                                Avg
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2446">
                                Confidence
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-2447">
                            {Array.from({
                        length: 7
                      }).map((_, i) => {
                        const min = Math.random() * 30 + 20;
                        const max = min + Math.random() * 40 + 20;
                        const avg = (min + max) / 2;
                        const confidence = Math.random() * 20 + 75;
                        const unit = selectedForecast.type === 'wind' ? 'm/s' : selectedForecast.type === 'solar' ? 'W/m²' : selectedForecast.type === 'temperature' ? '°C' : '€/MWh';
                        return <tr key={i} className="hover:bg-slate-50" data-id="element-2448">
                                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2449">
                                    Day {i + 1}
                                  </td>
                                  <td className="px-4 py-3 text-slate-600" data-id="element-2450">
                                    {min.toFixed(1)} {unit}
                                  </td>
                                  <td className="px-4 py-3 text-slate-600" data-id="element-2451">
                                    {max.toFixed(1)} {unit}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2452">
                                    {avg.toFixed(1)} {unit}
                                  </td>
                                  <td className="px-4 py-3" data-id="element-2453">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${confidence > 85 ? 'bg-green-100 text-green-700' : confidence > 75 ? 'bg-yellow-100 text-yellow-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-2454">
                                      {confidence.toFixed(0)}%
                                    </span>
                                  </td>
                                </tr>;
                      })}
                          </tbody>
                        </table>
                      </div>

                      {/* Forecast Notes */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2455">
                        <div className="flex gap-3" data-id="element-2456">
                          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={16} data-id="element-2457" />
                          <div className="text-sm text-blue-900" data-id="element-2458">
                            <p className="font-medium mb-1" data-id="element-2459">Forecast Notes</p>
                            <p className="text-blue-700" data-id="element-2460">
                              Real-time forecast data from EPT-2 proprietary
                              model, ECMWF, and GFS. The chart above shows
                              actual forecast data with model agreement and
                              uncertainty bands.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-2461">
                  <div className="text-xs text-slate-500" data-id="element-2462">
                    Data source: EPT-2 Model • Updated 15 min ago
                  </div>
                  <button onClick={handleCloseForecastModal} className="px-6 py-2 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-all" data-id="element-2463">
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence data-id="element-2464">
        {showShareModal && selectedMessageToShare && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={handleCloseShareModal} data-id="element-2465" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2466">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl pointer-events-auto overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-2467">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-pink-50 to-purple-50" data-id="element-2468">
                  <div className="flex items-start justify-between" data-id="element-2469">
                    <div className="flex items-center gap-3" data-id="element-2470">
                      <div className="p-2.5 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500" data-id="element-2471">
                        <Share2 size={20} className="text-white" data-id="element-2472" />
                      </div>
                      <div data-id="element-2473">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-2474">
                          Share Insight
                        </h3>
                        <p className="text-sm text-slate-600" data-id="element-2475">
                          Share this response with your team
                        </p>
                      </div>
                    </div>
                    <button onClick={handleCloseShareModal} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2476">
                      <X size={20} className="text-slate-400" data-id="element-2477" />
                    </button>
                  </div>
                </div>

                {/* Highlight Box */}
                <div className="p-6" data-id="element-2478">
                  <div className="relative" data-id="element-2479">
                    {/* Decorative gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-blue-500/5 rounded-2xl" data-id="element-2480" />
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-2xl blur-xl" data-id="element-2481" />

                    {/* Content box */}
                    <div className="relative bg-white/80 backdrop-blur-xl border-2 border-slate-200/50 rounded-2xl p-6 shadow-lg" data-id="element-2482">
                      {/* Jua Oracle badge */}
                      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-200/50" data-id="element-2483">
                        <div className="p-1.5 rounded-lg bg-gradient-to-br from-pink-100 to-purple-100" data-id="element-2484">
                          <Sparkles size={14} className="text-pink-600" data-id="element-2485" />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-2486">
                          Jua Oracle
                        </span>
                      </div>

                      {/* Message content */}
                      <div className="prose prose-sm max-w-none" data-id="element-2487">
                        <div className="text-sm text-slate-700 leading-relaxed space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar pr-2" data-id="element-2488">
                          {renderMarkdown(selectedMessageToShare.content)}
                        </div>
                      </div>

                      {/* Decorative corner accent */}
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-bl-full blur-2xl" data-id="element-2489" />
                    </div>
                  </div>
                </div>

                {/* Share Options */}
                <div className="p-6 border-t border-slate-200 bg-slate-50" data-id="element-2490">
                  <div className="grid grid-cols-3 gap-3" data-id="element-2491">
                    <button onClick={handleCopyURL} className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gradient-to-br hover:from-pink-50 hover:to-purple-50 border border-slate-200 hover:border-pink-300 rounded-xl transition-all group" data-id="element-2492">
                      <div className="p-2.5 rounded-lg bg-slate-100 group-hover:bg-gradient-to-br group-hover:from-pink-100 group-hover:to-purple-100 transition-all" data-id="element-2493">
                        <Link size={18} className="text-slate-600 group-hover:text-pink-600 transition-colors" data-id="element-2494" />
                      </div>
                      <span className="text-xs font-medium text-slate-700 group-hover:text-slate-900" data-id="element-2495">
                        Copy URL
                      </span>
                    </button>

                    <button onClick={handleCopyText} className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gradient-to-br hover:from-pink-50 hover:to-purple-50 border border-slate-200 hover:border-pink-300 rounded-xl transition-all group" data-id="element-2496">
                      <div className="p-2.5 rounded-lg bg-slate-100 group-hover:bg-gradient-to-br group-hover:from-pink-100 group-hover:to-purple-100 transition-all" data-id="element-2497">
                        <Copy size={18} className="text-slate-600 group-hover:text-pink-600 transition-colors" data-id="element-2498" />
                      </div>
                      <span className="text-xs font-medium text-slate-700 group-hover:text-slate-900" data-id="element-2499">
                        Copy Text
                      </span>
                    </button>

                    <button onClick={handleDownloadImage} className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gradient-to-br hover:from-pink-50 hover:to-purple-50 border border-slate-200 hover:border-pink-300 rounded-xl transition-all group" data-id="element-2500">
                      <div className="p-2.5 rounded-lg bg-slate-100 group-hover:bg-gradient-to-br group-hover:from-pink-100 group-hover:to-purple-100 transition-all" data-id="element-2501">
                        <Image size={18} className="text-slate-600 group-hover:text-pink-600 transition-colors" data-id="element-2502" />
                      </div>
                      <span className="text-xs font-medium text-slate-700 group-hover:text-slate-900" data-id="element-2503">
                        Save Image
                      </span>
                    </button>
                  </div>

                  {/* Success message */}
                  <AnimatePresence data-id="element-2504">
                    {copySuccess && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg" data-id="element-2505">
                        <Check size={14} className="text-green-600" data-id="element-2506" />
                        <span className="text-sm font-medium text-green-700" data-id="element-2507">
                          {copySuccess}
                        </span>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Send to Overview Toast */}
      <AnimatePresence data-id="element-2508">
        {showSendToast && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-6 right-6 z-[200] pointer-events-none" data-id="element-2509">
            <div className="bg-slate-900 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 pointer-events-auto" data-id="element-2510">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center" data-id="element-2511">
                <Check size={16} className="text-white" data-id="element-2512" />
              </div>
              <div data-id="element-2513">
                <p className="text-sm font-semibold" data-id="element-2514">{sendToastMessage}</p>
                <p className="text-xs text-slate-300" data-id="element-2515">
                  View in Overview dashboard
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Selection Menu - NEW */}
      <AnimatePresence data-id="element-2516">
        {selectionMenu.visible && <SelectionMenu position={selectionMenu.position} onSave={handleSaveFromSelection} data-id="element-2517" />}
      </AnimatePresence>

      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden justify-center items-stretch px-6 pb-4" data-id="element-2518">
        {/* Main Chat Area - add right margin to account for fixed sidebar */}
        <div className="flex-1 max-w-4xl flex flex-col bg-white/60 backdrop-blur-xl border border-slate-200/50 rounded-3xl shadow-lg shadow-slate-200/50 overflow-hidden lg:mr-[356px]" data-id="element-2519">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar min-h-0" data-source="Oracle Chat" data-id="element-2520">
            {messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60" data-id="element-2521">
                <MessageSquare size={48} className="mb-4" data-id="element-2522" />
                <p className="text-center" data-id="element-2523">
                  Ask a specific question to get started
                  <br data-id="element-2524" />
                  <span className="text-xs" data-id="element-2525">
                    Tip: Include timeframe and region for best results
                  </span>
                </p>
              </div> : messages.map((msg, idx) => {
            if (msg.role === 'user') {
              return <div key={idx} className="flex justify-end" data-id="element-2526">
                      <div className="max-w-[85%] rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 text-white rounded-tr-sm shadow-md p-4" data-source="User Query" data-id="element-2527">
                        <div className="text-sm space-y-2 leading-relaxed" data-id="element-2528">
                          <div className="whitespace-pre-wrap font-medium selection:bg-white/30" data-id="element-2529">
                            {msg.content}
                          </div>
                        </div>
                      </div>
                    </div>;
            } else {
              const sections = splitIntoSections(msg.content);
              return <div key={idx} className="space-y-3" data-message-index={idx} data-id="element-2530">
                      {sections.map((section, sectionIdx) => {
                  const isIntro = isIntroSection(section);
                  return <motion.div key={`${idx}-${sectionIdx}`} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="flex justify-start" data-id="element-2531">
                            <div className="max-w-[85%] rounded-2xl bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm p-4" data-source="Oracle Response" data-id="element-2532">
                              <div className="text-sm space-y-2 leading-relaxed selection:bg-pink-100 selection:text-pink-900" data-id="element-2533">
                                {renderMarkdown(section)}
                              </div>

                              {!isIntro && <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100" data-id="element-2534">
                                  <button onClick={() => handleSaveKeyPoint(section, idx * 100 + sectionIdx, 'general')} disabled={isExtractingInsights && extractionMessageIndex === idx * 100 + sectionIdx} className="p-2 rounded-lg transition-colors hover:bg-pink-50 text-slate-400 hover:text-pink-600 relative group disabled:opacity-50 disabled:cursor-not-allowed" title="Save insight" data-id="element-2535">
                                    {isExtractingInsights && extractionMessageIndex === idx * 100 + sectionIdx ? <RefreshCw size={14} className="animate-spin" data-id="element-2536" /> : <Bookmark size={14} data-id="element-2537" />}
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-2538">
                                      {isExtractingInsights && extractionMessageIndex === idx * 100 + sectionIdx ? 'Analyzing...' : 'Save insight'}
                                    </span>
                                  </button>
                                  <button className="p-2 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2539">
                                    <ThumbsUp size={14} data-id="element-2540" />
                                  </button>
                                  <button className="p-2 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2541">
                                    <ThumbsDown size={14} data-id="element-2542" />
                                  </button>
                                  <button onClick={() => handleOpenShareModal(section, idx * 100 + sectionIdx)} className="p-2 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors ml-auto" data-id="element-2543">
                                    <Share2 size={14} data-id="element-2544" />
                                  </button>
                                </div>}
                            </div>
                          </motion.div>;
                })}
                    </div>;
            }
          })}

            {isLoading && <>
                {/* Reasoning Display - hide once streaming starts */}
                <AnimatePresence data-id="element-2545">
                  {showReasoning && reasoningSteps.length > 0 && (() => {
                // Check if streaming has started (last message is assistant with content)
                const lastMessage = messages[messages.length - 1];
                const isStreaming = lastMessage?.role === 'assistant' && lastMessage?.content?.length > 0;
                // Hide reasoning once streaming starts
                if (isStreaming) {
                  return null;
                }
                return <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="flex justify-start" data-id="element-2546">
                          <div className="max-w-[85%] w-full" data-id="element-2547">
                            <ReasoningDisplay steps={reasoningSteps} isThinking={isLoading} data-id="element-2548" />
                          </div>
                        </motion.div>;
              })()}
                </AnimatePresence>

                {/* Loading dots - REMOVED: Never show, go straight from reasoning to streaming */}
              </>}

            {!isLoading && messages.length > 0 && suggestedFollowUps.length > 0 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex flex-wrap gap-2 mt-4 ml-2" data-id="element-2549">
                  {suggestedFollowUps.map((suggestion, idx) => <button key={idx} onClick={() => handleSendWithReasoning(stripMarkdown(suggestion))} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 border border-pink-200 hover:border-pink-400 text-pink-700 text-xs font-medium rounded-full transition-all hover:shadow-sm hover:-translate-y-0.5" data-id="element-2550">
                      <span data-id="element-2551">{stripMarkdown(suggestion)}</span>
                      <ArrowRight size={12} data-id="element-2552" />
                    </button>)}
                </motion.div>}
          </div>

          {/* Input Area */}
          <div className="flex-shrink-0 p-3 bg-white/80 backdrop-blur-md border-t border-slate-200/50" data-id="element-2553">
            {Object.keys(contextAnswers).length > 0 && Object.entries(contextAnswers).some(([_, value]) => value.trim() !== '') && <div className="mb-2 flex flex-wrap gap-2" data-id="element-2554">
                  {Object.entries(contextAnswers).filter(([_, value]) => value.trim() !== '').map(([key, value]) => <button key={key} onClick={() => setShowContextHelper(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full hover:bg-blue-100 hover:border-blue-300 transition-all group" data-id="element-2555">
                        <span className="font-medium" data-id="element-2556">{key}:</span>
                        <span data-id="element-2557">{value}</span>
                        <button onClick={e => {
                e.stopPropagation();
                setContextAnswers(prev => {
                  const updated = {
                    ...prev
                  };
                  delete updated[key];
                  return updated;
                });
              }} className="ml-0.5 p-0.5 hover:bg-blue-200 rounded-full transition-colors" aria-label={`Remove ${key} context`} data-id="element-2558">
                          <X size={12} className="text-blue-600" data-id="element-2559" />
                        </button>
                      </button>)}
                </div>}
            <div className="relative" data-id="element-2560">
              <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about spreads, volatility, or forecast divergence..." className="w-full min-h-[50px] max-h-[100px] pl-4 pr-16 py-3 rounded-2xl border border-slate-200 focus:border-pink-300 focus:ring-2 focus:ring-pink-500/20 outline-none resize-none text-slate-900 placeholder:text-slate-400 bg-white/50 custom-scrollbar leading-relaxed text-sm" data-id="element-2561" />
              <button onClick={handleSubmit} disabled={!query.trim() || isLoading} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:shadow-lg hover:shadow-pink-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2562">
                {isLoading ? <RefreshCw size={18} className="animate-spin" data-id="element-2563" /> : <Send size={18} data-id="element-2564" />}
              </button>
            </div>
            <div className="flex justify-between items-center mt-1.5 px-1" data-id="element-2565">
              {messages.length > 0 && <button onClick={clearHistory} className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors" data-id="element-2566">
                  <Trash2 size={10} data-id="element-2567" />
                  Clear
                </button>}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Fixed to viewport with dynamic positioning */}
        <div className={`hidden lg:flex flex-col w-[340px] gap-4 flex-shrink-0 fixed right-6 ${sidebarTop} ${sidebarBottom} overflow-y-auto custom-scrollbar z-40 transition-all duration-300`} data-id="element-2568">
          {/* Rotating Pro Tips - Fixed: only content animates */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-5 shadow-lg text-white relative overflow-hidden flex-shrink-0" data-id="element-2569">
            <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl -mr-10 -mt-10" data-id="element-2570" />

            <AnimatePresence mode="wait" data-id="element-2571">
              <motion.div key={currentProTipIndex} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="relative z-10" data-id="element-2572">
                <div className="flex items-center gap-2 mb-3" data-id="element-2573">
                  <div className="p-2 bg-white/10 rounded-lg" data-id="element-2574">
                    <currentProTip.icon size={18} className="text-pink-400" data-id="element-2575" />
                  </div>
                  <h3 className="font-semibold text-sm" data-id="element-2576">Pro Tip</h3>
                </div>
                <h4 className="font-bold mb-2 text-white" data-id="element-2577">
                  {currentProTip.title}
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed" data-id="element-2578">
                  {currentProTip.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Interactive Progress Dots */}
            <div className="flex gap-2 mt-4 relative z-10" data-id="element-2579">
              {proTips.map((_, idx) => <button key={idx} onClick={() => setCurrentProTipIndex(idx)} className={`h-2 rounded-full transition-all hover:bg-pink-300 ${idx === currentProTipIndex ? 'w-8 bg-pink-400' : 'w-2 bg-white/30 hover:bg-white/50'}`} aria-label={`Go to tip ${idx + 1}`} data-id="element-2580" />)}
            </div>
          </div>

          {/* Saved Key Points - Now with Send to Overview button */}
          <div className="bg-white/60 backdrop-blur-xl border border-slate-200/50 rounded-3xl p-5 shadow-sm shadow-slate-200/50 flex flex-col flex-shrink-0 max-h-[420px]" data-id="element-2581">
            <div className="flex items-center justify-between mb-4 flex-shrink-0" data-id="element-2582">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2" data-id="element-2583">
                <Bookmark size={14} className="text-pink-500" data-id="element-2584" />
                Saved Key Points
              </h3>
              {savedKeyPoints.length > 0 && <span className="px-2 py-0.5 bg-pink-100 text-pink-700 text-xs font-bold rounded-full" data-id="element-2585">
                  {savedKeyPoints.length}
                </span>}
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 min-h-0" data-source="Saved Insights" data-id="element-2586">
              {savedKeyPoints.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-8 px-6" data-id="element-2587">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2" data-id="element-2588">
                    No Saved Points Yet
                  </h4>
                  <p className="text-xs text-slate-500 leading-relaxed max-w-[280px]" data-id="element-2589">
                    Save key insights and trading opportunities by clicking the
                    bookmark icon on any Oracle response, or select text to save
                    it directly.
                  </p>
                </div> : savedKeyPoints.map(kp => <div key={kp.id} className="relative group" data-id="element-2590">
                    <button onClick={() => handleOpenKeyPointModal(kp)} className="w-full text-left p-3 bg-white border border-slate-200 rounded-xl hover:border-pink-200 hover:shadow-md transition-all" data-source={`Saved ${kp.type === 'trade-idea' ? 'Trade' : 'Insight'}`} data-id="element-2591">
                      <div className="flex items-start gap-2 mb-2" data-id="element-2592">
                        <div className={`p-1.5 rounded-lg flex-shrink-0 ${kp.type === 'trade-idea' ? 'bg-emerald-50' : 'bg-pink-50'}`} data-id="element-2593">
                          {kp.type === 'trade-idea' ? <ArrowUpDown size={12} className="text-emerald-600" data-id="element-2594" /> : <Bookmark size={12} className="text-pink-600" data-id="element-2595" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2596">
                          <p className="text-xs text-slate-700 leading-relaxed line-clamp-3 selection:bg-pink-100 selection:text-pink-900" data-id="element-2597">
                            {stripMarkdown(kp.content)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-2598">
                        <p className="text-[10px] text-slate-400" data-id="element-2599">
                          {formatTimeAgo(kp.timestamp)}
                        </p>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${kp.type === 'trade-idea' ? 'bg-emerald-100 text-emerald-700' : 'bg-pink-100 text-pink-700'}`} data-id="element-2600">
                          {kp.type === 'trade-idea' ? 'Trade' : 'Insight'}
                        </span>
                      </div>
                    </button>

                    {/* 3-dot menu - top right */}
                    <div className="absolute top-2 right-2 z-20" onClick={e => e.stopPropagation()} data-id="element-2601">
                      <button onClick={e => {
                  e.stopPropagation();
                  setOpenDropdownId(openDropdownId === kp.id ? null : kp.id);
                }} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100" data-id="element-2602">
                        <MoreVertical size={14} className="text-slate-400" data-id="element-2603" />
                      </button>

                      {/* Dropdown menu */}
                      <AnimatePresence data-id="element-2604">
                        {openDropdownId === kp.id && <motion.div initial={{
                    opacity: 0,
                    scale: 0.95,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    scale: 0.95,
                    y: -10
                  }} className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-30" data-id="element-2605">
                            <button onClick={e => {
                      e.stopPropagation();
                      handleShareMessage(kp.fullContent, kp.messageIndex);
                      setOpenDropdownId(null);
                    }} className="w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-id="element-2606">
                              <Share2 size={12} data-id="element-2607" />
                              Share
                            </button>
                            <button onClick={e => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(stripMarkdown(kp.fullContent));
                      setOpenDropdownId(null);
                    }} className="w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-id="element-2608">
                              <Copy size={12} data-id="element-2609" />
                              Copy text
                            </button>
                            <button onClick={e => {
                      e.stopPropagation();
                      handleDeleteKeyPoint(kp.id, e);
                      setOpenDropdownId(null);
                    }} className="w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-2610">
                              <Trash2 size={12} data-id="element-2611" />
                              Delete
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>)}
            </div>

            {/* NEW: Send to Overview Button */}
            {savedKeyPoints.length > 0 && <div className="mt-4 pt-4 border-t border-slate-200 flex-shrink-0" data-id="element-2612">
                <button onClick={handleSendToOverview} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md" data-id="element-2613">
                  <Upload size={16} data-id="element-2614" />
                  Send to Overview
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-2" data-id="element-2615">
                  This will clear the list and send all insights to Overview
                </p>
              </div>}
          </div>

          {/* Suggested Queries - Now execute immediately */}
          <div className="bg-white/60 backdrop-blur-xl border border-slate-200/50 rounded-3xl p-5 shadow-sm shadow-slate-200/50 flex-shrink-0" data-id="element-2616">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2617">
              <Lightbulb size={14} className="text-amber-500" data-id="element-2618" />
              Quick Queries
            </h3>

            <div className="space-y-2" data-id="element-2619">
              {suggestedQueries.map(suggestion => <button key={suggestion.id} onClick={() => handleSendWithReasoning(suggestion.text)} disabled={isLoading} className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 bg-white/50 hover:border-pink-300 hover:bg-pink-50/50 transition-all text-xs text-slate-700 hover:text-slate-900 group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2620">
                  <div className="relative z-10" data-id="element-2621">{suggestion.text}</div>
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-50/0 via-pink-50/50 to-pink-50/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" data-id="element-2622" />
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}